'use client';

/**
 * MagicInput Component
 *
 * A text input with animated focus glow and smooth transitions.
 */

import InputAdornment from '@mui/material/InputAdornment';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef, useState } from 'react';
import { baseColors } from '../../theme/palette';
import { focusRings } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export interface MagicInputProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Glow color on focus
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary';

  /**
   * Show success state
   * @default false
   */
  success?: boolean;

  /**
   * Show warning state — amber border + amber helperText. Use this for
   * "this field is recommended for the next loan-stage advance" or any
   * other "you can ship without it but you probably shouldn't" scenarios.
   * Distinct from `error` (red, blocking) so users can tell at-a-glance
   * which fields are hard-required vs. soft-required.
   *
   * Precedence: `error` wins over `warning` if both are set.
   * @default false
   */
  warning?: boolean;

  /**
   * Icon to show on the left
   */
  startIcon?: React.ReactNode;

  /**
   * Icon to show on the right
   */
  endIcon?: React.ReactNode;

  /**
   * Animated label (floats above input)
   * @default true
   */
  animatedLabel?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $isDark: boolean;
  $glowColor: 'primary' | 'secondary';
  $isFocused: boolean;
  $hasValue: boolean;
  $success: boolean;
  $hasError: boolean;
  $hasWarning: boolean;
  $size: 'small' | 'medium';
}

// Filter out transient props (prefixed with $) to prevent them from being passed to the DOM
const shouldForwardProp = (prop: string) => !prop.startsWith('$');

const StyledTextField = styled(TextField, { shouldForwardProp })<StyledTextFieldProps>(
  ({ theme, $isDark, $glowColor, $isFocused, $success, $hasError, $hasWarning, $size }) => {
    // Use theme colors for primary/secondary to respect theme overrides
    const primaryColor = theme.palette.primary.main;
    const secondaryColor = theme.palette.secondary.main;
    const focusColor = $glowColor === 'primary' ? primaryColor : secondaryColor;
    const successColor = theme.palette.success.main;
    const errorColor = theme.palette.error.main;
    const warningColor = theme.palette.warning.main;

    let borderColor = $isDark ? 'rgba(255, 255, 255, 0.15)' : baseColors.neutral[300];
    let glowShadow = 'none';

    // Precedence: error > warning > success > focus. error stays the loudest
    // signal because it's blocking; warning is informational ("recommended").
    if ($hasError) {
      borderColor = errorColor;
      glowShadow = focusRings.error;
    } else if ($hasWarning) {
      borderColor = warningColor;
      glowShadow = `0 0 0 3px ${alpha(warningColor, 0.22)}`;
    } else if ($success) {
      borderColor = successColor;
      glowShadow = `0 0 0 3px ${alpha(successColor, 0.25)}`;
    } else if ($isFocused) {
      borderColor = focusColor;
      glowShadow = `0 0 0 3px ${alpha(focusColor, 0.18)}, inset 0 0.5px 0 rgba(255, 255, 255, 0.5)`;
    }

    const isMaterial = theme.designGuideline === 'material';

    // ── Material Design 3 ──
    if (isMaterial) {
      return {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8, // MD3 text field radius
          backgroundColor: 'transparent',
          transition: transitionPresets.input,

          '& fieldset': {
            borderColor: $hasError
              ? errorColor
              : $success
                ? successColor
                : $isDark
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(0,0,0,0.12)',
            borderWidth: $isFocused || $hasError || $success ? 2 : 1,
            transition: transitionPresets.input,
          },

          '&:hover fieldset': {
            borderColor: $hasError
              ? errorColor
              : $success
                ? successColor
                : $isDark
                  ? 'rgba(255,255,255,0.20)'
                  : 'rgba(0,0,0,0.20)',
          },

          '&.Mui-focused': {
            '& fieldset': {
              borderColor: $hasError ? errorColor : $success ? successColor : focusColor,
              borderWidth: 2,
            },
          },

          '& input': {
            padding: $size === 'small' ? '8.5px 14px' : '12px 16px',
            fontSize: '0.875rem', // MD3 body-large
            minHeight: '20px',
            '&::placeholder': {
              color: $isDark ? 'rgba(255,255,255,0.5)' : baseColors.neutral[400],
              opacity: 1,
            },
          },

          '& textarea': {
            '&::placeholder': {
              color: $isDark ? 'rgba(255,255,255,0.5)' : baseColors.neutral[400],
              opacity: 1,
            },
          },

          // Select trigger — extra vertical padding for multi-line rendered values
          '& .MuiSelect-select': {
            paddingTop: $size === 'small' ? 14 : 28,
            paddingBottom: $size === 'small' ? 10 : 28,
          },
        },

        '& .MuiInputLabel-root': {
          color: $isDark ? 'rgba(255,255,255,0.6)' : baseColors.neutral[500],
          fontSize: $size === 'small' ? '0.8125rem' : '0.875rem',
          transition: transitionPresets.allFast,

          '&.MuiInputLabel-shrink': {
            backgroundColor: $isDark ? theme.palette.background.paper : theme.palette.background.default,
            padding: '0 6px',
            marginLeft: '-4px',
            borderRadius: 2,
          },

          '&.Mui-focused': {
            color: $hasError ? errorColor : $hasWarning ? warningColor : $success ? successColor : focusColor,
          },

          '&.Mui-error': {
            color: errorColor,
          },

          ...($hasWarning && !$hasError ? { color: warningColor } : {}),
        },

        '& .MuiFormHelperText-root': {
          marginLeft: 4,
          marginTop: 6,
          ...($hasWarning && !$hasError ? { color: warningColor } : {}),
        },
      };
    }

    // ── Apple Liquid Glass (original) ──
    return {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        transition: transitionPresets.input,

        '& fieldset': {
          borderColor,
          borderWidth: $isFocused || $hasError || $hasWarning || $success ? 1.5 : 0.5,
          transition: transitionPresets.input,
        },

        '&:hover fieldset': {
          borderColor: $hasError
            ? errorColor
            : $hasWarning
              ? warningColor
              : $success
                ? successColor
                : $isDark
                  ? 'rgba(255, 255, 255, 0.3)'
                  : alpha(primaryColor, 0.5),
        },

        '&.Mui-focused': {
          backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

          '& fieldset': {
            borderColor: $hasError ? errorColor : $hasWarning ? warningColor : $success ? successColor : focusColor,
            boxShadow: glowShadow,
          },
        },

        // Input padding and font size — 44px large, 36px medium touch targets
        '& input': {
          padding: $size === 'small' ? '8.5px 14px' : '11.5px 16px',
          fontSize: $size === 'small' ? '0.875rem' : '0.9375rem',
          minHeight: '20px',
          // Placeholder color for dark mode visibility
          '&::placeholder': {
            color: $isDark ? 'rgba(255, 255, 255, 0.5)' : baseColors.neutral[400],
            opacity: 1, // Firefox requires explicit opacity
          },
        },

        // Textarea (multiline) placeholder styling
        '& textarea': {
          '&::placeholder': {
            color: $isDark ? 'rgba(255, 255, 255, 0.5)' : baseColors.neutral[400],
            opacity: 1, // Firefox requires explicit opacity
          },
        },

        // Select trigger — extra vertical padding for multi-line rendered values
        '& .MuiSelect-select': {
          paddingTop: $size === 'small' ? 14 : 28,
          paddingBottom: $size === 'small' ? 10 : 28,
        },
      },

      // Label styling
      '& .MuiInputLabel-root': {
        color: $isDark ? 'rgba(255, 255, 255, 0.6)' : baseColors.neutral[500],
        fontSize: $size === 'small' ? '0.8125rem' : '0.9375rem',
        transition: transitionPresets.allFast,

        // Add background when label is shrunk (floating on border)
        '&.MuiInputLabel-shrink': {
          backgroundColor: $isDark ? 'rgba(28,28,30,0.85)' : 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          padding: '0 6px',
          marginLeft: '-4px',
          borderRadius: 4,
        },

        '&.Mui-focused': {
          color: $hasError ? errorColor : $hasWarning ? warningColor : $success ? successColor : focusColor,
        },

        '&.Mui-error': {
          color: errorColor,
        },

        // Warning state — applies even when not focused/errored. Mirrors how
        // MUI's `Mui-error` cascade sets a static label color so the "this
        // field needs attention" treatment is visible at rest.
        ...($hasWarning && !$hasError ? { color: warningColor } : {}),
      },

      // Helper text
      '& .MuiFormHelperText-root': {
        marginLeft: 4,
        marginTop: 6,
        ...($hasWarning && !$hasError ? { color: warningColor } : {}),
      },
    };
  },
);

// Glow wrapper for focus animation
const GlowWrapper = styled(motion.div)({
  position: 'relative',
  width: '100%',
});

// Icon wrapper with animation
const IconWrapper = styled(motion.div)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ============================================================================
// Component
// ============================================================================

export const MagicInput = forwardRef<HTMLInputElement, MagicInputProps>(
  (
    {
      glowColor = 'primary',
      success = false,
      warning = false,
      startIcon,
      endIcon,
      animatedLabel = true,
      error,
      size = 'medium',
      onFocus,
      onBlur,
      value,
      slotProps,
      ...props
    },
    ref,
  ) => {
    // MUI v9 Autocomplete's `renderInput` params still use the legacy
    // `InputProps` / `InputLabelProps` / `inputProps` prop names. MUI 9
    // dropped these from `TextFieldProps` so they don't exist on our
    // destructure — read them off props via a widened view and remap to
    // the new `slotProps` API. Without this the Autocomplete ref chain
    // breaks and the dropdown never opens ("Unable to find the input
    // element").
    const legacyProps = props as typeof props & {
      InputProps?: { ref?: React.Ref<HTMLElement> };
      InputLabelProps?: object;
      inputProps?: { ref?: React.Ref<HTMLInputElement> };
      inputRef?: React.Ref<HTMLInputElement>;
    };
    const legacyInputProps = legacyProps.InputProps;
    const legacyInputLabelProps = legacyProps.InputLabelProps;
    const legacyHtmlInputProps = legacyProps.inputProps;
    // MUI 9's Autocomplete attaches the native-input ref via
    // `params.inputProps.ref`. Forward it through TextField's top-level
    // `inputRef` prop where MUI still looks for it. Without this forward,
    // Autocomplete logs "Unable to find the input element" and the
    // popper never opens.
    const forwardedInputRef = legacyProps.inputRef ?? legacyHtmlInputProps?.ref;
    const { InputProps: _i, InputLabelProps: _il, inputProps: _hi, inputRef: _ir, ...cleanProps } = legacyProps;
    void _i;
    void _il;
    void _hi;
    void _ir;
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = Boolean(value);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Extract slotProps if they're objects (not functions). Legacy
    // `InputProps` / `inputProps` / `InputLabelProps` (MUI v5 API, still
    // used by Autocomplete's `renderInput` params) merge in as the BASE
    // — caller's explicit `slotProps.*` wins.
    const slotInputProps =
      slotProps?.input && typeof slotProps.input === 'object'
        ? { ...(legacyInputProps as object | undefined), ...slotProps.input }
        : (legacyInputProps as object | undefined);
    const slotHtmlInputProps =
      slotProps?.htmlInput && typeof slotProps.htmlInput === 'object'
        ? { ...(legacyHtmlInputProps as object | undefined), ...slotProps.htmlInput }
        : (legacyHtmlInputProps as object | undefined);
    const slotInputLabelProps =
      slotProps?.inputLabel && typeof slotProps.inputLabel === 'object'
        ? { ...(legacyInputLabelProps as object | undefined), ...slotProps.inputLabel }
        : (legacyInputLabelProps as object | undefined);

    // Build the internal adornments
    const internalStartAdornment = startIcon ? (
      <InputAdornment position="start">
        <AnimatePresence mode="wait">
          <IconWrapper
            key={isFocused ? 'focused' : 'unfocused'}
            initial={{ opacity: 0.5, scale: 0.95 }}
            animate={{
              opacity: isFocused ? 1 : 0.6,
              scale: 1,
              color: isFocused
                ? glowColor === 'primary'
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main
                : undefined,
            }}
            transition={{ duration: 0.15 }}
          >
            {startIcon}
          </IconWrapper>
        </AnimatePresence>
      </InputAdornment>
    ) : undefined;

    const internalEndAdornment = endIcon ? (
      <InputAdornment position="end">
        <IconWrapper
          animate={{
            opacity: isFocused ? 1 : 0.6,
          }}
          transition={{ duration: 0.15 }}
        >
          {endIcon}
        </IconWrapper>
      </InputAdornment>
    ) : undefined;

    // Merge slotProps.input from caller
    // Filter out disableUnderline as it's not valid for outlined variant
    const slotInputRecord = slotInputProps as Record<string, unknown> | undefined;
    const callerStart = slotInputRecord?.['startAdornment'] as React.ReactNode | undefined;
    const callerEnd = slotInputRecord?.['endAdornment'] as React.ReactNode | undefined;

    const { disableUnderline: _, ...filteredSlotInputProps } = (slotInputProps || {}) as Record<string, unknown>;

    const mergedInputSlotProps = {
      ...filteredSlotInputProps,
      startAdornment: callerStart || internalStartAdornment,
      endAdornment: callerEnd || internalEndAdornment,
    };

    // When `select` is used, MUI renders a Menu of the children (MenuItems).
    // By default MenuItem uses body1 (1rem) — larger than the input text
    // (0.875–0.9375rem), which breaks the size contract. Inject size-matched
    // typography into the Menu's Paper sx while letting callers override.
    const {
      select: isSelect,
      SelectProps: legacySelectProps,
      ...restCleanProps
    } = cleanProps as typeof cleanProps & {
      select?: boolean;
      SelectProps?: Record<string, unknown>;
    };
    // Merge MUI 9 `slotProps.select` and legacy `SelectProps` (deprecated
    // but still common in pre-9 codebases). When both are present, the
    // `slotProps.select` form wins — but legacy keys (e.g. `native: true`)
    // are merged in so consumers using the old API don't silently lose
    // configuration. Without this merge, the `mergedSelectSlot` we build
    // below (with MenuProps for size styling) ends up overriding the
    // consumer's `SelectProps` entirely on TextField (regression
    // surfaced by CreateApplicationModal's `SelectProps={{ native: true }}`).
    const callerSelectSlot = {
      ...(legacySelectProps ?? {}),
      ...((slotProps as Record<string, unknown> | undefined)?.['select'] as Record<string, unknown> | undefined),
    } as Record<string, unknown>;
    const menuItemFontSize = size === 'small' ? '0.875rem' : '0.9375rem';
    const menuItemMinHeight = size === 'small' ? 36 : 40;
    const callerMenuProps = (callerSelectSlot['MenuProps'] as Record<string, unknown> | undefined) ?? {};
    const callerMenuSlotProps = (callerMenuProps['slotProps'] as Record<string, unknown> | undefined) ?? {};
    const callerPaperSlot = (callerMenuSlotProps['paper'] as Record<string, unknown> | undefined) ?? {};
    const callerPaperSx = callerPaperSlot['sx'];
    const mergedSelectSlot = isSelect
      ? {
          ...callerSelectSlot,
          MenuProps: {
            ...callerMenuProps,
            slotProps: {
              ...callerMenuSlotProps,
              paper: {
                ...callerPaperSlot,
                sx: [
                  {
                    '& .MuiMenuItem-root': {
                      fontSize: menuItemFontSize,
                      minHeight: menuItemMinHeight,
                    },
                  },
                  ...(Array.isArray(callerPaperSx) ? callerPaperSx : callerPaperSx ? [callerPaperSx] : []),
                ],
              },
            },
          },
        }
      : Object.keys(callerSelectSlot).length > 0
        ? callerSelectSlot
        : undefined;

    return (
      <GlowWrapper
        animate={{
          scale: isFocused ? 1.005 : 1,
        }}
        transition={{ duration: 0.15, ease: [0.215, 0.61, 0.355, 1] }}
      >
        <StyledTextField
          ref={ref}
          inputRef={forwardedInputRef}
          $isDark={isDark}
          $glowColor={glowColor}
          $isFocused={isFocused}
          $hasValue={hasValue}
          $success={success}
          $hasError={Boolean(error)}
          $hasWarning={Boolean(warning) && !error}
          $size={size}
          error={error}
          value={value}
          size={size}
          onFocus={handleFocus}
          onBlur={handleBlur}
          variant="outlined"
          fullWidth
          select={isSelect}
          slotProps={{
            input: mergedInputSlotProps,
            htmlInput: slotHtmlInputProps || undefined,
            inputLabel: {
              shrink: animatedLabel ? undefined : true,
              ...(typeof props.label === 'string' ? { title: props.label } : {}),
              ...(slotInputLabelProps as Record<string, unknown>),
            },
            ...(mergedSelectSlot ? { select: mergedSelectSlot } : {}),
          }}
          {...restCleanProps}
        />
      </GlowWrapper>
    );
  },
);

MagicInput.displayName = 'MagicInput';

export default MagicInput;
